import * as THREE from "three";

const VR_MODEL_PROFILE_KEY = "__artetraVRModelPerformance";

type MeshProfile = {
  castShadow: boolean;
  receiveShadow: boolean;
  frustumCulled: boolean;
  material: THREE.Material | THREE.Material[];
  simplifiedMaterial?: THREE.Material | THREE.Material[];
};

export type VRModelRenderStats = {
  meshCount: number;
  materialCount: number;
  triangleCount: number;
};

export const getVRModelRenderStats = (object: THREE.Object3D): VRModelRenderStats => {
  const materials = new Set<THREE.Material>();
  const stats: VRModelRenderStats = {
    meshCount: 0,
    materialCount: 0,
    triangleCount: 0,
  };

  object.traverse((child: any) => {
    if (!child?.isMesh || !child.geometry?.isBufferGeometry) return;

    stats.meshCount++;
    const geometry = child.geometry as THREE.BufferGeometry;
    const indexCount = geometry.getIndex?.()?.count;
    const positionCount = geometry.getAttribute?.("position")?.count;
    const vertexCount = Number.isFinite(indexCount) ? Number(indexCount) : Number(positionCount);
    if (Number.isFinite(vertexCount)) {
      stats.triangleCount += Math.floor(Number(vertexCount) / 3);
    }

    const meshMaterials = Array.isArray(child.material) ? child.material : child.material ? [child.material] : [];
    meshMaterials.forEach((material: THREE.Material) => materials.add(material));
  });

  stats.materialCount = materials.size;
  return stats;
};

export const shouldSimplifyModelForVR = (object: THREE.Object3D) => {
  const stats = getVRModelRenderStats(object);
  return stats.triangleCount >= 120000 || stats.materialCount >= 24 || stats.meshCount >= 48;
};

const createSimplifiedMaterial = (material: any) => {
  const simplified = new THREE.MeshBasicMaterial({
    color: material?.color?.isColor ? material.color.clone() : new THREE.Color(0xffffff),
    map: material?.map || null,
    alphaMap: material?.alphaMap || null,
    transparent: Boolean(material?.transparent || Number(material?.opacity) < 1),
    opacity: Number.isFinite(Number(material?.opacity)) ? Number(material.opacity) : 1,
    alphaTest: Number.isFinite(Number(material?.alphaTest)) ? Number(material.alphaTest) : 0,
    side: Number.isFinite(Number(material?.side)) ? material.side : THREE.FrontSide,
    vertexColors: Boolean(material?.vertexColors),
    fog: Boolean(material?.fog),
  });
  simplified.name = material?.name ? `${material.name}-vr-basic` : "artetra-vr-basic";
  return simplified;
};

const disposeMaterialOnly = (material: THREE.Material | THREE.Material[] | undefined) => {
  if (!material) return;
  if (Array.isArray(material)) {
    material.forEach((entry) => entry?.dispose?.());
    return;
  }
  material.dispose?.();
};

export const applyVRModelPerformanceProfile = (
  object: THREE.Object3D,
  options: { simplifyMaterials?: boolean } = {}
) => {
  object.traverse((child: any) => {
    if (!child?.isMesh) return;

    child.userData = child.userData || {};
    let profile = child.userData[VR_MODEL_PROFILE_KEY] as MeshProfile | undefined;
    if (!profile) {
      profile = {
        castShadow: Boolean(child.castShadow),
        receiveShadow: Boolean(child.receiveShadow),
        frustumCulled: Boolean(child.frustumCulled),
        material: child.material,
      };
      child.userData[VR_MODEL_PROFILE_KEY] = profile;
    }

    child.castShadow = false;
    child.receiveShadow = false;
    child.frustumCulled = true;

    if (options.simplifyMaterials && child.material && !profile.simplifiedMaterial) {
      profile.simplifiedMaterial = Array.isArray(child.material)
        ? child.material.map(createSimplifiedMaterial)
        : createSimplifiedMaterial(child.material);
      child.material = profile.simplifiedMaterial;
    }

    const materials = Array.isArray(child.material) ? child.material : child.material ? [child.material] : [];
    materials.forEach((material: any) => {
      if (material) material.needsUpdate = true;
    });
  });
};

export const restoreVRModelPerformanceProfile = (object: THREE.Object3D) => {
  object.traverse((child: any) => {
    const profile = child?.userData?.[VR_MODEL_PROFILE_KEY] as MeshProfile | undefined;
    if (!profile) return;

    const simplifiedMaterial = profile.simplifiedMaterial;
    child.castShadow = profile.castShadow;
    child.receiveShadow = profile.receiveShadow;
    child.frustumCulled = profile.frustumCulled;
    child.material = profile.material;
    disposeMaterialOnly(simplifiedMaterial);
    delete child.userData[VR_MODEL_PROFILE_KEY];
  });
};

export const getVRModelOriginalMaterial = (object: any): THREE.Material | THREE.Material[] | undefined => {
  return object?.userData?.[VR_MODEL_PROFILE_KEY]?.material;
};
